import React from "react";
import { Link } from "react-router-dom";
import { MarketingNav } from "@/components/marketing/MarketingNav";
import { MarketingFooter } from "@/components/marketing/MarketingFooter";

export function PrivacyPage() {
  const lastUpdated = "February 14, 2026";

  return (
    <div className="cb-root">
      <MarketingNav />

      <section className="mx-auto w-full max-w-3xl px-5 pt-24 pb-20 sm:px-8 sm:pt-32">
        <div className="mb-10">
          <p data-testid="cb-privacy-updated" className="text-xs font-semibold uppercase tracking-[0.22em] text-[#c8ff00]">
            Last updated: {lastUpdated}
          </p>
          <h1
            data-testid="cb-privacy-heading"
            className="cb-display mt-3 text-4xl leading-[1.05] text-white sm:text-5xl"
          >
            Privacy Policy
          </h1>
          <p className="mt-4 text-base leading-relaxed text-[#8a8a90]">
            ClippingBase (&quot;we&quot;, &quot;us&quot;, &quot;our&quot;) operates the website{" "}
            <a href="https://clippingbase.com" className="text-white underline hover:text-[#c8ff00]">clippingbase.com</a>{" "}
            and the ClippingBase creator platform. This Privacy Policy explains what
            information we collect, how we use it, and the rights you have over your data.
          </p>
        </div>

        <div className="cb-legal-body space-y-8 text-[15px] leading-relaxed text-[#c8c8cc]">
          <section>
            <h2 className="mb-3 text-xl font-bold text-white">1. Information we collect</h2>
            <p className="mb-2">We collect the following categories of information:</p>
            <ul className="ml-5 list-disc space-y-1.5">
              <li><span className="font-semibold text-white">Account information</span> — email address, chosen username, hashed password, and (if you sign in with Discord) your Discord username, avatar and Discord ID.</li>
              <li><span className="font-semibold text-white">Social account information</span> — the handles and platform names (Instagram, TikTok, YouTube) you connect for clip submissions, plus any OAuth tokens you grant us (Instagram Login, YouTube, TikTok).</li>
              <li><span className="font-semibold text-white">Public content metrics</span> — public counters (views, likes, plays, comments) of clips you submit to campaigns. We fetch these via the connected platform&apos;s official API or through a public web-scraper.</li>
              <li><span className="font-semibold text-white">Payout details</span> — the payout method address you provide (PayPal e-mail, wallet address, etc.).</li>
              <li><span className="font-semibold text-white">Technical data</span> — IP address, browser, device type, session cookies, timestamps and anonymised analytics events.</li>
            </ul>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-white">2. Instagram, TikTok and YouTube connections</h2>
            <p>
              When you connect a social account through the &quot;Connect Instagram&quot; (or equivalent)
              flow, the platform asks you to grant limited permissions. We store the access
              token securely and use it only to (a) confirm the account belongs to you,
              (b) read the public metrics of the specific clips you submit to a campaign
              on ClippingBase, and (c) show you your dashboard analytics. We{" "}
              <span className="font-semibold text-white">do not</span> post to, message from, or read private data
              of your accounts. You can disconnect any social account any time from{" "}
              <Link to="/socials" className="text-[#c8ff00] underline">Settings → Accounts</Link>.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-white">3. How we use your information</h2>
            <ul className="ml-5 list-disc space-y-1.5">
              <li>Provide the ClippingBase service, verify your identity and match your clips to campaigns.</li>
              <li>Track submission metrics so brands can pay you accurately.</li>
              <li>Send transactional emails and in-app notifications (verification codes, payout status, appeal outcomes).</li>
              <li>Detect fraud, abuse and duplicate accounts.</li>
              <li>Comply with legal obligations and tax reporting.</li>
            </ul>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-white">4. Sharing of information</h2>
            <p className="mb-2">We share information only in these limited cases:</p>
            <ul className="ml-5 list-disc space-y-1.5">
              <li><span className="font-semibold text-white">With brands</span> — the brand of a campaign you submit to receives your username, connected handles and clip metrics. Your email and password are never shared.</li>
              <li><span className="font-semibold text-white">With processors</span> — payment processors (e.g., PayPal, Stripe), transactional email (e.g., Resend), and analytics providers, strictly to operate the service.</li>
              <li><span className="font-semibold text-white">Legal</span> — when required by law, subpoena or to protect users.</li>
            </ul>
            <p className="mt-2">We never sell your personal information.</p>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-white">5. Data retention</h2>
            <p>
              We retain your account data for as long as your account is active. You can
              request deletion at any time (see section 8) and we will remove your personal
              data within 30 days, except where retention is required for tax or fraud
              obligations.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-white">6. Cookies</h2>
            <p>
              We use a session cookie to keep you signed in and functional cookies to
              remember your preferences (theme, sidebar state). We do not run
              advertising trackers.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-white">7. Your rights (GDPR / CCPA)</h2>
            <p>You have the right to:</p>
            <ul className="ml-5 list-disc space-y-1.5">
              <li>Access a copy of the personal data we hold about you.</li>
              <li>Correct or update inaccurate data.</li>
              <li>Request deletion of your data.</li>
              <li>Object to processing or withdraw consent for OAuth connections.</li>
              <li>Port your data to another service.</li>
            </ul>
            <p className="mt-2">
              To exercise any of these rights, email{" "}
              <a href="mailto:privacy@clippingbase.com" className="text-[#c8ff00] underline">
                privacy@clippingbase.com
              </a>.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-white">8. Deleting your account &amp; data</h2>
            <p>
              You can permanently delete your ClippingBase account and all associated
              data by following the steps at{" "}
              <Link to="/data-deletion" className="text-[#c8ff00] underline">
                clippingbase.com/data-deletion
              </Link>
              . For revoking any social connection you can also do it inside the
              respective platform (e.g., Instagram → Settings → Apps and Websites).
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-white">9. Children</h2>
            <p>
              ClippingBase is not directed at children under 13 (or 16 in the EU/UK).
              We do not knowingly collect personal information from children.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-white">10. Changes to this policy</h2>
            <p>
              We may update this policy from time to time. Material changes will be
              notified via in-app notice and/or email. The &quot;Last updated&quot; date at the
              top of this page reflects the current version.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-white">11. Contact</h2>
            <p>
              Questions about this policy? Email{" "}
              <a href="mailto:privacy@clippingbase.com" className="text-[#c8ff00] underline">
                privacy@clippingbase.com
              </a>{" "}
              or use the{" "}
              <Link to="/contact" className="text-[#c8ff00] underline">contact form</Link>.
            </p>
          </section>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
